import {
    useListKpiTargetsQuery, useListKpiTasksQuery, useDeleteKpiTargetMutation
} from "apis/kpiApi";
import {useSelector} from "react-redux";
import {Column} from "primereact/column";
import React, { useState, useEffect, useRef } from "react";
import {Button} from "primereact/button";
import {useListCompanyAccountsQuery} from "../../apis/companiesApi";
import {TreeTable} from "primereact/treetable";
import _ from 'lodash';
import {useNavigate} from "react-router-dom";
import {formatTargetValue} from "../../hooks/kpiHooks";
import {FilterMatchMode} from "primereact/api";
import BaseTable from "../../components/tables/BaseTable";
import {useUserProfileQuery} from "../../apis/authenticationApi";
import { useResponsibleHiearchicalList } from 'hooks/taskHooks';
import timeFrames from "data/kpi-time-frames.json";
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { Toast } from 'primereact/toast';


const KPITargetTable = ({predefinedSelectedView, predefinedKpis, hideNewButton, disableCarryOnYearKpis}) => {
    const navigate = useNavigate();
    const {data: kpiTargetsHook, isFetching: isFetchingKpiTargets} = useListKpiTargetsQuery();
    const kpiTargets = predefinedKpis ?? kpiTargetsHook;
    const [deleteKpiTarget] = useDeleteKpiTargetMutation();
    const {data: companyUsers, isFetching: isFetchingCompanyUsers} = useListCompanyAccountsQuery();
    const {data: tasks, isLoading: isLoadingTasks} = useListKpiTasksQuery();
    const selectedYear = useSelector(state => state.kpi.selectedYear);
    const selectedViewHook = useSelector(state => state.kpi.selectedView);
    const selectedView = predefinedSelectedView ?? selectedViewHook;
    const {data: userProfile} = useUserProfileQuery();
    const hierarchyUsers = useResponsibleHiearchicalList(userProfile?.company?.account, {excludeSameUserId:  userProfile?.company?.role !== "CompanyOwner"});
    const [treeTableData, setTreeTableData] = useState([]);
    const toast = useRef(null);


    const edit = (id) => {
        navigate(`edit/${id}`);
    }

    const deleteEntry = (id, event) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Are you sure you want to proceed? All the associated tasks of the KPI will be deleted as well.',
            icon: 'pi pi-exclamation-triangle',
            accept: () => deleteKpiTarget(id)
        });
    }

    const responsibleBodyTemplate = (rowData) => {
        const user = (companyUsers || []).find(user => user.id === rowData?.data?.responsible);

        return user ? (user?.registered ? `${user?.firstName} ${user?.lastName}` : user?.email) :
            "-";
    }

    const goalBodyTemplate = (rowData) => {
        return (<div className="kpi-goal-name">{rowData?.data?.name}</div>)
    }

    const actionTemplate = (rowData) => {
        const canEdit = userProfile?.company?.role === "CompanyOwner" || (
            userProfile?.company?.role === "CompanyAdmin" &&
                rowData?.data?.level !== "Corporate" && (hierarchyUsers || []).find(e => e.id === rowData?.data?.responsible)
        );
        return (
            <>
                <Button type="button" icon={"pi " +  (canEdit? "pi-pencil" : "pi-eye")} onClick={() => edit(rowData.key)} />
                {canEdit && <Button type="button" icon="pi pi-times" onClick={(e) => deleteEntry(rowData.key, e)} />}
            </>
        );
    }

    const calculateTreeTableDataChildren = (parent, kpis) => {
        return kpis.filter(target => target.parent === parent).map(target => {
            return {
                key: target.id,
                data: target,
                children: calculateTreeTableDataChildren(target.id, kpis)
            }
        })
    }

    const flattenTreeTable = (node) => {
        if(node.data.timeFrame === 'Annual' || node.data.timeFrame === 'Quarter' || selectedView !== 1) {
            return [node];
        }

        return node.children.flatMap(child => flattenTreeTable(child));
    }

    const calculateTreeTableDataFolded = () => {
        const clonedKpis = _.cloneDeep((kpiTargets || []).filter(target => target.year === selectedYear));
        const grouped = _.groupBy(clonedKpis, target => target.type + " " + target.name);

        return Object.values(grouped)
            .map(groupedKpi =>
                groupedKpi.reduce((previousValue, currentValue) => {
                    if(!currentValue.parent || !previousValue.ids || !previousValue.ids.find(val => val.timeFrame === currentValue.timeFrame && val.id === currentValue.parent))
                        previousValue[`${currentValue.timeFrame}Value`] = formatTargetValue(currentValue, currentValue.targetValue, "*", true);

                    previousValue.ids = (previousValue.ids || [{id: previousValue.id, timeFrame: previousValue.timeFrame}]);
                    previousValue.ids.push({id: currentValue.id, timeFrame: currentValue.timeFrame});

                    return previousValue;
                }))
            .map(groupedKpi => {
                groupedKpi[`${groupedKpi.timeFrame}Value`] = formatTargetValue(groupedKpi, groupedKpi.targetValue, "*", true);

                return groupedKpi;
            });
    }


    useEffect(() => {
        const carryOnYear = ({timeFrame, year}) => (
            !disableCarryOnYearKpis && year && (timeFrames.find(e => e.id && e.id === timeFrame)?.carryOnYears || []).map(e => year + (e - 1)) || []
        );
        const kpis = _.cloneDeep((kpiTargets || []).filter(target => target.year === selectedYear || carryOnYear(target).includes(selectedYear)));
        setTreeTableData(calculateTreeTableDataChildren("", kpis).flatMap(node => flattenTreeTable(node)));
    }, [selectedYear, kpiTargets, selectedView]);

    const header = (
        <div className="table-header">
            {!hideNewButton &&
                <div className="button-panel">
                    <Button type="button" label="New" icon="pi pi-plus" onClick={()  => edit("new")} />
                </div>
            }
        </div>
    );

    const isLoading = isFetchingKpiTargets || isFetchingCompanyUsers || isLoadingTasks;

    if(selectedView === 0) {
        return (
            <div className="kpi-target-flat-table">
                <BaseTable
                    isLoading={isLoading}
                    createNew={()  => edit("new")}
                    emptyMessage="No targets found."
                    globalFilterFields={['name']}
                    fieldFilters={{
                        'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
                        'name': { value: null, matchMode: FilterMatchMode.STARTS_WITH }
                    }}
                    dataList={calculateTreeTableDataFolded()}>
                    <Column field="name" header="Goal" filter filterPlaceholder="Search by goal" ></Column>
                    <Column field="QuarterValue" header="Quarter"></Column>
                    <Column field="AnnualValue" header="Annual"></Column>
                    <Column field="ThreeYearValue" header={(selectedYear + 2) + " (3-Year)"}></Column>
                    <Column field="FiveYearValue"  header={(selectedYear + 4) + " (5-Year)"}></Column>
                    <Column field="TenYearValue"  header={(selectedYear + 9) + " (10-Year)"}></Column>
                </BaseTable>
            </div>
        )
    }

    const timeframeRenderer = (rowData) => {
        const year = rowData?.data?.year ?? selectedYear;
        if(rowData.data.timeFrame === 'TenYear') return (year + 9) + " (10-Year)";
        if(rowData.data.timeFrame === 'FiveYear') return (year + 4) + " (5-Year)";
        if(rowData.data.timeFrame === 'ThreeYear') return (year + 2) + " (3-Year)";
        if(rowData.data.timeFrame === 'Annual') return "Annual";
        if(rowData.data.timeFrame === 'Quarter') return rowData.data.quarter;

        return "";
    }

    const taskCountRenderer = (rowData) => {
        const targetTasks = tasks.filter(task => task.parent === rowData.data?.id);
        const closedTasks = targetTasks.reduce((total, task) => task.taskStatus === 'Done' ? total + 1 : total, 0);

        return `${closedTasks}/${targetTasks.length}`;
    }

    return (
        <div className="kpi-target-table">
            <Toast ref={toast} />
            <ConfirmPopup />
            <div className="mobile-horizontal-scrollable">
                <TreeTable header={header} value={treeTableData} responsivelayout="scroll" emptyMessage="No KPI Targets" isLoading={isLoading}>
                    <Column expander field="name" header="Goal" body={goalBodyTemplate} ></Column>
                    <Column field="weight" header="Weight"></Column>
                    {selectedView !== 1 && <Column field="timeFrame" header="Time Frame" body={timeframeRenderer}></Column>}
                    <Column field="targetValue" header="Value" body={(rowData) => formatTargetValue(rowData.data, rowData.data?.targetValue, selectedView !== 1 || rowData.data?.timeFrame === 'Annual' ? "*" : "")}></Column>
                    {selectedView === 1 && <Column field="targets.0" header="Q1" body={(rowData) => formatTargetValue(rowData.data, rowData.data?.targets[0], rowData.data?.timeFrame === 'Quarter' && rowData.data?.quarter === 'Q1' ? "*" : "")}></Column>}
                    {selectedView === 1 && <Column field="targets.1" header="Q2" body={(rowData) => formatTargetValue(rowData.data, rowData.data?.targets[1], rowData.data?.timeFrame === 'Quarter' && rowData.data?.quarter === 'Q2' ? "*" : "")}></Column>}
                    {selectedView === 1 && <Column field="targets.2" header="Q3" body={(rowData) => formatTargetValue(rowData.data, rowData.data?.targets[2], rowData.data?.timeFrame === 'Quarter' && rowData.data?.quarter === 'Q3' ? "*" : "")}></Column>}
                    {selectedView === 1 && <Column field="targets.3" header="Q4" body={(rowData) => formatTargetValue(rowData.data, rowData.data?.targets[3], rowData.data?.timeFrame === 'Quarter' && rowData.data?.quarter === 'Q4' ? "*" : "")}></Column>}
                    {selectedView === 1 && <Column field="taskCount" header="Tasks" body={taskCountRenderer} />}
                    <Column field="responsible" header="Responsible" body={responsibleBodyTemplate}></Column>
                    <Column className="action-column" headerClassName="centered-header" bodyClassName="p-text-center" header="Actions" body={actionTemplate}></Column>
                </TreeTable>
            </div>
        </div>
    )
}

export default KPITargetTable;
